import axios from 'axios';
import { Semaphore } from 'async-mutex';
import { convertFromNewUiToQueryFormat } from '@hotjar/global-filters';
import type { QuantzRequestBodyDto, QuantzResponseDto, SuccessQuantzResponseDto } from './dto';
import { mapDtoToModel, mapFeedbackDtoToModel, mapClickedElementDtoToModel } from './mapper';
import type { QuantzRequest, QuantzResponse } from '../model';
import { QuantzProperty, QuantzEventType } from '../model';
import { getUTCOffset } from './getUTCOffset';

export const API_ROOT = `/api/dashboard/v1/sites`;
// maximum number of dashboard api requests to allow at a time.
const MAX_CONCURRENT_REQUESTS = 5;

function isSuccess(response: QuantzResponseDto): response is SuccessQuantzResponseDto {
  return response.meta.success;
}

const semaphore = new Semaphore(MAX_CONCURRENT_REQUESTS);

export async function getData(request: QuantzRequest): Promise<QuantzResponse> {
  return semaphore.runExclusive(async () => {
    const isFeedbackRequest =
      request.groupBy === QuantzProperty.FEEDBACK_SCORE ||
      request.property === QuantzProperty.FEEDBACK_SCORE;
    const isClickedElementRequest =
      request.groupBy === QuantzProperty.TEXT && request.eventType === QuantzEventType.MOUSE_CLICK;
    const isEventRequest = request.eventType !== undefined;
    const path = `${API_ROOT}/${request.siteId}/query${isEventRequest ? '/event' : ''}`;
    const body: QuantzRequestBodyDto = {
      query: {
        session_filters: convertFromNewUiToQueryFormat(request.sessionFilters),
        operation: request.operation,
        group_by: request.groupBy,
        event_filters: request.eventFilters,
        limit: request.limit,
        level: request.level,
        windowed: request.windowed,
        event_type: request.eventType,
        property: request.property,
        tz_offset: getUTCOffset(),
      },
    };
    const response = await axios.post<QuantzResponseDto>(path, body);
    const dto: QuantzResponseDto = response.data;
    if (!isSuccess(dto)) {
      throw new Error(`Error retrieving quantz data: ${dto?.errors?.join(', ')}`);
    }

    if (isFeedbackRequest) {
      return mapFeedbackDtoToModel(dto);
    }

    if (isClickedElementRequest) {
      return mapClickedElementDtoToModel(dto, request.limit);
    }

    return mapDtoToModel(dto);
  });
}
